import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Download, Lock } from 'lucide-react';
import { comptabiliteApi } from '../../services/comptabiliteApi';
import { formatCurrency } from '../../utils/formatters';
import { downloadFile } from '../../utils/download';

const num = (s: string | number | null | undefined) => Number(s ?? 0);

const Ligne: React.FC<{ label: string; montant: number; bold?: boolean }> = ({ label, montant, bold }) => (
  <div className={`flex justify-between px-4 py-1.5 text-sm ${bold ? 'font-semibold text-gray-900 border-t border-gray-200' : 'text-gray-700'}`}>
    <span>{label}</span>
    <span>{formatCurrency(montant)}</span>
  </div>
);

const ClotureExercice: React.FC = () => {
  const queryClient = useQueryClient();
  const { data: exercices = [] } = useQuery({
    queryKey: ['compta', 'exercices'],
    queryFn: () => comptabiliteApi.getExercices(),
  });
  // Exercice ouvert le plus récent
  const exercice = exercices.filter((e) => !e.cloture).sort((a, b) => b.date_debut.localeCompare(a.date_debut))[0];

  const { data: apercu } = useQuery({
    queryKey: ['compta', 'resultat-exercice', exercice?.id],
    queryFn: () => comptabiliteApi.getResultatExercice(exercice!.id),
    enabled: !!exercice,
  });

  const cloturer = useMutation({
    mutationFn: () => comptabiliteApi.cloturerExercice(exercice!.id),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['compta'] });
      alert(`${res.message}\nRésultat net : ${formatCurrency(num(res.resultat_net))}`);
    },
    onError: (e: any) => alert(e?.response?.data?.exercice || "Échec de la clôture."),
  });

  if (!exercice) return null;

  return (
    <div className="lg:col-span-2 bg-white rounded-xl shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
        <h3 className="font-semibold text-gray-800 flex items-center gap-2">
          <Lock className="h-4 w-4 text-gray-500" /> Clôture de l'exercice {exercice.libelle}
        </h3>
        <span className="text-xs text-gray-400">{exercice.date_debut} → {exercice.date_fin}</span>
      </div>
      <div className="p-4 flex flex-wrap items-center justify-between gap-4">
        {apercu ? (
          <div className="flex gap-8 text-sm">
            <div><p className="text-gray-500">Produits</p><p className="font-semibold text-gray-900">{formatCurrency(num(apercu.total_produits))}</p></div>
            <div><p className="text-gray-500">Charges</p><p className="font-semibold text-gray-900">{formatCurrency(num(apercu.total_charges))}</p></div>
            <div><p className="text-gray-500">Résultat net</p>
              <p className={`font-bold ${num(apercu.resultat_net) >= 0 ? 'text-green-700' : 'text-red-700'}`}>{formatCurrency(num(apercu.resultat_net))}</p>
            </div>
          </div>
        ) : <span className="text-sm text-gray-500">Calcul du résultat…</span>}
        <button
          onClick={() => {
            if (window.confirm(`Clôturer l'exercice ${exercice.libelle} ? Les comptes de gestion seront soldés vers le résultat et l'exercice deviendra définitif (plus aucune écriture possible sur cette période).`)) {
              cloturer.mutate();
            }
          }}
          disabled={cloturer.isPending}
          className="inline-flex items-center gap-1.5 bg-gray-800 hover:bg-black text-white text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-50"
        >
          <Lock className="h-4 w-4" /> {cloturer.isPending ? 'Clôture…' : "Clôturer l'exercice"}
        </button>
      </div>
    </div>
  );
};

const EtatsFinanciers: React.FC = () => {
  const { data: resultat } = useQuery({ queryKey: ['compta', 'resultat'], queryFn: () => comptabiliteApi.getCompteResultat() });
  const { data: bilan } = useQuery({ queryKey: ['compta', 'bilan'], queryFn: () => comptabiliteApi.getBilan() });

  return (
    <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-6">
      <ClotureExercice />
      {/* Compte de résultat */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
          <h3 className="font-semibold text-gray-800">Compte de résultat</h3>
          <button onClick={() => downloadFile('/comptabilite/rapports/export/', 'compte_resultat.xlsx', { etat: 'compte_resultat' })}
            className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900">
            <Download className="h-3.5 w-3.5" /> Excel
          </button>
        </div>
        {resultat ? (
          <div className="py-2">
            <p className="px-4 py-1 text-xs font-medium text-gray-400 uppercase">Produits</p>
            {resultat.produits.map((p) => <Ligne key={p.numero} label={`${p.numero} ${p.intitule}`} montant={num(p.montant)} />)}
            <Ligne label="Total produits" montant={num(resultat.total_produits)} bold />
            <p className="px-4 py-1 mt-2 text-xs font-medium text-gray-400 uppercase">Charges</p>
            {resultat.charges.map((c) => <Ligne key={c.numero} label={`${c.numero} ${c.intitule}`} montant={num(c.montant)} />)}
            <Ligne label="Total charges" montant={num(resultat.total_charges)} bold />
            <div className={`flex justify-between px-4 py-2 mt-2 font-bold border-t-2 ${num(resultat.resultat_net) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
              <span>Résultat net</span>
              <span>{formatCurrency(num(resultat.resultat_net))}</span>
            </div>
          </div>
        ) : <div className="p-6 text-gray-500">Chargement…</div>}
      </div>

      {/* Bilan */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
          <h3 className="font-semibold text-gray-800">Bilan</h3>
          <div className="flex items-center gap-3">
            {bilan && <span className={`text-xs ${bilan.equilibre ? 'text-green-600' : 'text-amber-600'}`}>{bilan.equilibre ? 'équilibré' : 'écart'}</span>}
            <button onClick={() => downloadFile('/comptabilite/rapports/export/', 'bilan.xlsx', { etat: 'bilan' })}
              className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900">
              <Download className="h-3.5 w-3.5" /> Excel
            </button>
          </div>
        </div>
        {bilan ? (
          <div className="py-2">
            <p className="px-4 py-1 text-xs font-medium text-gray-400 uppercase">Actif</p>
            {bilan.actif.map((a) => <Ligne key={a.numero} label={`${a.numero} ${a.intitule}`} montant={num(a.montant)} />)}
            <Ligne label="Total actif" montant={num(bilan.total_actif)} bold />
            <p className="px-4 py-1 mt-2 text-xs font-medium text-gray-400 uppercase">Passif</p>
            {bilan.passif.map((p) => <Ligne key={p.numero} label={`${p.numero} ${p.intitule}`} montant={num(p.montant)} />)}
            <Ligne label="Total passif" montant={num(bilan.total_passif)} bold />
          </div>
        ) : <div className="p-6 text-gray-500">Chargement…</div>}
      </div>
    </div>
  );
};

export default EtatsFinanciers;
